import { useTranslation } from 'react-i18next';
import { Select, Modal, Spin, Button, Row, Col, DatePicker } from 'antd';
import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';

import { downloadCSVFromUrl } from '../../../helpers/utilityHelper';
import { normalize } from '../../../utils/normalizeWords';
import DataTable from '../../../components/common/DataTable';
import { usePermissions } from '../../../hooks/usePermissions';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { disableFutureDates } from '../../../utils/disableDates';
import {
  downloadAllCitizenInspectionDetails,
  getAllCitizenInspectionDetailsById,
  getAllCitizenReportedPriceDetails,
} from './getCitizenReportedData';
import DefaultButton from '../../../components/buttons/DefaultButton';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import CitizenReportedPricesView, { PriceReviewConfirmHandle } from './CitizenReportedPricesView';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';

export interface priceRecordType {
  id: number;
  price: string;
  productRemarks?: string;
  rejectRemark?: string;
  wasInSuggestedPresentation?: string;
  wasUnderSuggestedBrand?: string;
  altPresentation?: {
    altUnit: string;
  };
  altBrand?: string;
  photoOrInvoice: string;
  refId: string;
  isActive: boolean;
  status: string;
  reportedTime: string;
  coordinates?: any;
  createTime?: string;
  updateTime?: string;
  product: {};
  source?: string;
}

export interface pricesStatus {
  refId: string;
  status: string;
  remark?: string;
}

const CitizenReportedInspectionList = () => {
  const { t } = useTranslation(['inspector', 'common']);

  const [inspectionData, setInspectionData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingCitizenInspections, setIsLoadingCitizenInspections] = useState<boolean>(false);
  const [pageSize] = useState<number>(8);
  const [totalInspections, setTotalInspections] = useState<number>(0);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [selectedInspection, setSelectedInspection] = useState<any>(null);
  const [isLoadingInspectionDetails, setIsLoadingInspectionDetails] = useState<boolean>(false);
  const [isdownloadingInspections, setIsdownloadingInspections] = useState<boolean>(false);
  const { successNotification, errorNotification } = useNotificationContext();

  const [selectedEstablishmentType, setSelectedEstablishmentType] = useState<string | undefined>(
    undefined
  );
  const [selectedEstablishment, setSelectedEstablishment] = useState<string | undefined>(undefined);
  const [selectedEstablishmentIds, setSelectedEstablishmentIds] = useState<number[]>([]);

  const [establishmentData, setEstablishmentData] = useState<
    Array<{ positionType: string; positionName: string; name: string; id: number; locationId: number; }>
  >([]);

  const [establishmentTypes, setEstablishmentTypes] = useState<{label: string, value: string}[]>([]);
  const [establishments, setEstablishments] = useState<string[]>([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);

  const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(undefined);
  const [selectedMunicipality, setSelectedMunicipality] = useState<string | undefined>(undefined);
  const [departments, setDepartments] = useState<string[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [locations, setLocations] = useState<Array<{ district: string; city: string; id: number }>>(
    []
  );
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const { hasPermission } = usePermissions();

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>();

  const [selectedInspectionId, setSelectedInspectionId] = useState();
  const [selectedFilters, setSelectedFilters] = useState();
  const [disableReviewConfirm, setDisableReviewConfirm] = useState<boolean>(true);
  const [selectedInspectionPrices, setSelectedInspectionPrices] = useState<priceRecordType[]>([]);
  const priceViewRef = useRef<PriceReviewConfirmHandle>(null);
  const { isOnline } = useIsOnlineContext();

  const enableDisableReviewConfirm = (hasStatusChanges: boolean) => {
    hasStatusChanges ? setDisableReviewConfirm(false) : setDisableReviewConfirm(true);
  };

  const getAllCitizenInspectionList = useCallback(
    async (page: number = currentPage, filters?: any) => {
      setIsLoadingCitizenInspections(true);
      try {
        const paginationParams = {
          page,
          limit: pageSize,
        };

        const filterParams: any = {
          ...paginationParams,
          // timezoneOffset: new Date().getTimezoneOffset(),
        };

        if (
          filters?.establishmentIds &&
          Array.isArray(filters?.establishmentIds) &&
          filters?.establishmentIds.length > 0
        ) {
          filterParams.establishmentIds = filters?.establishmentIds;
        }

        if (
          filters?.locationIds &&
          Array.isArray(filters?.locationIds) &&
          filters?.locationIds.length > 0
        ) {
          filterParams.locationIds = filters?.locationIds;
        }

        if (filters?.dateRange && filters?.dateRange[0]) {
          filterParams.startTime = filters?.dateRange[0].valueOf();
        }

        if (filters?.dateRange && filters?.dateRange[1]) {
          filterParams.endTime = filters?.dateRange[1].endOf('day').valueOf();
        }

        // const response = await fetcher.get(
        //   'inspection/list',
        //   filterParams,
        //   {},
        //   ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
        // );

        const response = await getAllCitizenReportedPriceDetails(filterParams);

        if (response.statusText === 'SUCCESS') {
          const inspections = response.data?.data || response.data || [];
          const total = response.data?.total || 0;
          setInspectionData(inspections);
          setTotalInspections(total);
          setCurrentPage(page);
        }
      } catch (error) {
        errorNotification(t('citizenReportedPriceFetchError'));
      } finally {
        setIsLoadingCitizenInspections(false);
      }
    },
    [pageSize, errorNotification, t]
  );

  const downloadCSVFn = async () => {
    try {
      setIsdownloadingInspections(true);
      let filterParams: any = {};

      if (selectedEstablishmentIds.length > 0) {
        filterParams.establishmentIds = selectedEstablishmentIds;
      }
      const response = await downloadAllCitizenInspectionDetails(filterParams);

      if (response.statusText === 'SUCCESS') {
        const { url, fileName } = response.data;
        downloadCSVFromUrl(url, fileName);
      } else {
        console.error(t('citizenReportedPriceDownloadError'), response);
        errorNotification(t('citizenReportedPriceDownloadError'));
      }
    } catch (error) {
      console.error(t('citizenReportedPriceDownloadError'), error);
      errorNotification(t('citizenReportedPriceDownloadError'));
      setIsdownloadingInspections(false);
    } finally {
      setIsdownloadingInspections(false);
    }
  };

  const handlePageChange = (page: number) => {
    const filters = {
      establishmentIds: selectedEstablishmentIds,
      locationIds: selectedLocationIds,
      dateRange,
    };
    getAllCitizenInspectionList(page, filters);
  };

  const handleCitizenReportedPriceView = (inspectionId: any) => {
    setSelectedInspectionId(inspectionId);
    setIsViewModalVisible(true);
    loadCitizenInspectionDetailsById(inspectionId);
  };

  const loadCitizenInspectionDetailsById = async (inspectionId: any) => {
    setIsLoadingInspectionDetails(true);
    setIsViewModalVisible(true);
    try {
      const response = await getAllCitizenInspectionDetailsById(inspectionId);

      if (response?.statusText === 'SUCCESS' && response?.data) {
        setSelectedInspection(response?.data);
        setSelectedInspectionPrices(response?.data?.prices?.flat(2));
      }
    } catch (error) {
      errorNotification(t('citizenReportedPriceFetchError'));
    } finally {
      setIsLoadingInspectionDetails(false);
    }
  };

  /* Close or Cancel View Price Modal */
  const handlePriceReviewCancel = () => {
    setIsViewModalVisible(false);
    setSelectedInspection(null);
  };

  /* Confirm button in view Price modal clicked */
  const handleReviewConfirm = () => {
    priceViewRef?.current?.handlePriceReviewConfirm();
    setIsViewModalVisible(false);
  };

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);

    try {
      const locationsData = await referenceDataSyncService.getLocations();
      const formattedLocations = locationsData.map((location: Record<string, unknown>) => ({
        district: location.district as string,
        city: location.city as string,
        id: location.id as number,
      }));
      setLocations(formattedLocations);

      const uniqueDepartments = Array.from(
        new Set(formattedLocations.map((location) => location.district))
      );
      setDepartments(uniqueDepartments);

      const establishmentData = await referenceDataSyncService.getEstablishments();
      const formattedEstablishmentData = establishmentData.map((establishment: any) => ({
        positionType: (establishment.establishmentType?.key || establishment.establishmentType?.nameEn || establishment.establishmentType || '') as string,
        positionName: (establishment.establishmentType?.nameEn || establishment.establishmentType?.key || establishment.establishmentType || '') as string,
        name: establishment.name as string,
        id: establishment.id as number,
        locationId: establishment.locationId as number,
      }));
      setEstablishmentData(formattedEstablishmentData);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [errorNotification]);

  /* Clear all Filters */
  const clearAllFilters = () => {
    setSelectedEstablishmentType(undefined);
    setSelectedEstablishment(undefined);
    setSelectedDepartment(undefined);
    setSelectedMunicipality(undefined);
    setDateRange(undefined);
    getAllCitizenInspectionList(1, {});
  };

  useEffect(() => {
    getAllCitizenInspectionList(1);
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    const filters: any = {
      establishmentIds: selectedEstablishmentIds,
      locationIds: selectedLocationIds,
      dateRange,
    };

    setSelectedFilters(filters);

    const hasFilters =
      (dateRange !== undefined && dateRange !== null) ||
      selectedLocationIds.length > 0 ||
      selectedEstablishmentIds.length > 0;

    if (hasFilters) {
      getAllCitizenInspectionList(1, filters);
      setEnableClearFiltersBtn(true);
    } else {
      getAllCitizenInspectionList(1, {});
      setEnableClearFiltersBtn(false);
    }
  }, [selectedEstablishmentIds, selectedLocationIds, dateRange]);

  useEffect(() => {
    let validEstablishments = establishmentData;

    if (selectedLocationIds && selectedLocationIds.length > 0) {
      validEstablishments = validEstablishments.filter((est) => selectedLocationIds.includes(est.locationId));
    }

    const uniqueTypesMap = new Map<string, string>();
    validEstablishments.forEach((est) => {
      if (est.positionType) {
        uniqueTypesMap.set(est.positionType, est.positionName || est.positionType);
      }
    });
    
    setEstablishmentTypes(
      Array.from(uniqueTypesMap.entries()).map(([key, name]) => ({
        label: name,
        value: key,
      }))
    );

    if (selectedEstablishmentType) {
      validEstablishments = validEstablishments.filter((est) => est.positionType === selectedEstablishmentType);
    }

    setEstablishments(Array.from(new Set(validEstablishments.map((est) => est.name))));

    let finalEstablishmentsForApi = validEstablishments;
    
    if (selectedEstablishment) {
      finalEstablishmentsForApi = finalEstablishmentsForApi.filter((est) => est.name === selectedEstablishment);
    }

    if (selectedEstablishmentType || selectedEstablishment) {
      setSelectedEstablishmentIds(finalEstablishmentsForApi.map((est) => est.id));
    } else {
      setSelectedEstablishmentIds([]);
    }
  }, [establishmentData, selectedLocationIds, selectedEstablishmentType, selectedEstablishment]);

  /* Define Citizen Inspection table columns */
  const columns = [
    {
      key: 'department',
      title: t('department'),
      dataIndex: 'department',
      width: 150,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.department || 'N/A'}</span>
      ),
    },
    {
      key: 'municipality',
      title: t('municipality'),
      dataIndex: 'municipality',
      width: 150,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.municipality || 'N/A'}</span>
      ),
    },
    {
      key: 'establishmentType',
      title: t('establishmentType'),
      dataIndex: 'establishmentType',
      width: 150,
      render: (value: any, record: any) => {
        const typeStr = record?.establishment_type as string;
        const matched = establishmentTypes.find((t) => t.value === typeStr);
        return (
          <span className="font-medium">
            {matched ? matched.label : typeStr || 'N/A'}
          </span>
        );
      },
    },
    {
      key: 'establishment',
      title: t('establishment'),
      dataIndex: 'establishment',
      width: 80,
      align: 'left' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.establishment_name || 'N/A'}</span>
      ),
    },
    {
      key: 'submittedBy',
      title: t('submittedBy'),
      dataIndex: 'submittedBy',
      width: 80,
      align: 'left' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {record?.user_firstName || record?.user_fullName || 'N/A'} {record?.user_lastName}
        </span>
      ),
    },
    {
      key: 'recordedPrices',
      title: t('recordedPrices'),
      dataIndex: 'recordedPrices',
      width: 100,
      align: 'right' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">{record?.price_count || 'N/A'}</span>
      ),
    },
    {
      key: 'reportedTime',
      title: t('reportedTime'),
      dataIndex: 'reportedTime',
      align: 'center' as const,
      width: 200,
      render: (value: any) => (
        <span className="text-sm text-gray-600">
          {value ? dayjs(new Date(Number(value))).format('YYYY-MM-DD hh:mm:ss A') : 'N/A'}
        </span>
      ),
    },
    /* {
      key: 'location',
      title: t('location'),
      dataIndex: 'location',
      width: 200,
      render: (value: any, record: any) => {
        const location = record.inspection?.location;
        if (!location) {
          return <span className="text-gray-600">N/A</span>;
        }
        return (
          <span className="text-sm text-gray-700">
            {location.district && location.city
              ? `${location.district}, ${location.city}`
              : location.name || 'N/A'}
          </span>
        );
      },
    }, */
  ];

  /* Define table actions */
  const actions = [
    {
      key: 'view',
      label: t('view'),
      type: 'primary' as const,
      onClick: (record: any) => handleCitizenReportedPriceView(record.id),
    },
  ];

  useEffect(() => {
    if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
      const filteredMunicipalities = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.city);
      setMunicipalities(filteredMunicipalities);
      setSelectedMunicipality(undefined);
    } else {
      setMunicipalities([]);
      setSelectedMunicipality(undefined);
      setSelectedLocationIds([]);
    }
  }, [selectedDepartment, locations]);

  useEffect(() => {
    if (selectedMunicipality && selectedDepartment) {
      const locationIdsForSelectedMunicipality = locations
        .filter(
          (location) =>
            location.district === selectedDepartment && location.city === selectedMunicipality
        )
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedMunicipality) {
      const locationIdsForSelectedMunicipality = locations
        .filter((location) => location.city === selectedMunicipality)
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
    } else {
      setSelectedLocationIds([]);
    }
  }, [selectedMunicipality]);

  return (
    <>
      <section className="mb-8 mt-12 w-full">
        <span className="flex w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-0">
          <p className="mt-3 text-base font-medium text-headings-font-color">
            {t('citizenSubmittedPriceRecords')}
          </p>
        </span>
      </section>

      {/* Filters */}
      <section className="mb-8 w-full">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
            <Button
              type="link"
              onClick={clearAllFilters}
              className="hover:text-primary-dark text-sm text-primary"
              disabled={!enableClearFiltersBtn}
            >
              {t('clearFilters')}
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('department')}</label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('departmentFieldPlaceholder')}
                allowClear
                onClear={() => setSelectedDepartment(undefined)}
                value={selectedDepartment || undefined}
                onChange={setSelectedDepartment}
                options={departments.map((dept) => ({
                  label: dept,
                  value: dept,
                }))}
                loading={isLoadingFilterData}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('municipality')}</label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('municipalityFieldPlaceholder')}
                allowClear
                onClear={() => setSelectedMunicipality(undefined)}
                value={selectedMunicipality || undefined}
                onChange={setSelectedMunicipality}
                disabled={!selectedDepartment || municipalities.length === 0}
                options={municipalities.map((municipality) => ({
                  label: municipality,
                  value: municipality,
                }))}
                loading={isLoadingFilterData}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('establishmentType')}
                </label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('establishmentTypePlaceholder')}
                allowClear
                onClear={() => setSelectedEstablishmentType(undefined)}
                value={selectedEstablishmentType || undefined}
                onChange={setSelectedEstablishmentType}
                options={establishmentTypes}
                loading={isLoadingFilterData}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('establishment')}</label>
              </div>
              <Select
                className="w-full [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('establishmentPlaceholder')}
                allowClear
                onClear={() => setSelectedEstablishment(undefined)}
                value={selectedEstablishment || undefined}
                onChange={setSelectedEstablishment}
                disabled={!selectedEstablishmentType || establishments.length === 0}
                options={establishments.map((establishment) => ({
                  label: establishment,
                  value: establishment,
                }))}
                loading={isLoadingFilterData}
                showSearch
                filterOption={(input, option) =>
                  normalize(option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(normalize(input.toLowerCase()))
                }
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700">{t('dateRange')}</label>
              </div>
              <DatePicker.RangePicker
                className="w-full [&>.ant-picker]:!rounded-lg"
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                allowClear={true}
                disabledDate={disableFutureDates}
                placeholder={[t('from'), t('to')]}
              />
            </Col>
          </Row>
          {/* <Row className="mt-4 text-sm" justify="end" gutter={8}>
            <PrimaryButton
              handleOnClick={downloadCSVFn || !isOnline}
              disabled={isdownloadingInspections}
              className="px-3"
            >
              {t('downloadCSV')}
            </PrimaryButton>
          </Row> */}
        </div>
      </section>

      {/* Citizen reported Inspection list */}
      <section className="mb-6 w-full">
        <DataTable
          data={inspectionData.flat(2)}
          columns={columns}
          actions={actions}
          // actions={hasPermission(PermissionEnum.REVIEW_PRICE_REQUESTS) ? actions : []}
          loading={isLoadingCitizenInspections}
          totalItems={totalInspections}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          rowKey="id"
          showSizeChanger={false}
          showQuickJumper={true}
          showTotal={(total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`}
          className="submitted-price-records-table price-table"
          scroll={{ x: 1000 }}
        />
      </section>

      {/* View modal for Citizen reported prices for products in the selected Inspection */}
      <Modal
        title={<h2 className="text-md md:text-xl">{t('citizenPriceRecordModalTitle')}</h2>}
        open={isViewModalVisible}
        onCancel={handlePriceReviewCancel}
        footer={
          <div className="px-1 md:px-2">
            <Row justify="end" className="my-2 gap-x-4">
              <Col xs={24} sm={12} md={8} lg={4} className="mb-4 md:mb-0">
                <DefaultButton handleOnClick={handlePriceReviewCancel} width="100%">
                  {hasPermission(PermissionEnum.REVIEW_PRICE_REQUESTS) ? t('cancel') : t('close')}
                </DefaultButton>
              </Col>
              {hasPermission(PermissionEnum.REVIEW_PRICE_REQUESTS) && !disableReviewConfirm && (
                <Col xs={24} sm={12} md={8} lg={4} className="">
                  <PrimaryButton
                    handleOnClick={handleReviewConfirm}
                    width="100%"
                    hideBgGradient={true}
                    disabled={disableReviewConfirm}
                  >
                    {t('confirm')}
                  </PrimaryButton>
                </Col>
              )}
            </Row>
          </div>
        }
        width="95%"
        centered
        destroyOnHidden
        styles={{
          body: {
            padding: 0,
            // maxHeight: 'calc(70vh-500px)',
            // maxHeight: '70vh',
            // overflow:'auto',
            // height: '75vh',
            // maxHeight: 'calc(100vh - 200px)',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            // overflow:'hidden'
          },
        }}
      >
        {isLoadingInspectionDetails ? (
          <div className="flex h-full w-full flex-1 items-center justify-center py-8">
            <Spin size="large" />
          </div>
        ) : selectedInspection ? (
          <CitizenReportedPricesView
            t={t}
            selectedInspectionData={selectedInspection}
            selectedInspectionPricesData={selectedInspectionPrices}
            selectedInspectionId={selectedInspectionId}
            ref={priceViewRef}
            enableDisableReviewConfirm={enableDisableReviewConfirm}
          />
        ) : (
          <div className="py-8 text-center text-gray-500">{t('noPriceDetailsAvailable')}</div>
        )}
      </Modal>
    </>
  );
};

export default CitizenReportedInspectionList;
